import { Skeleton, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { useIsDesktop, useIsSmallScreen } from '../../../common/hooks/useResponsive';

interface ContentArticleViewSkeletonProps {
  // Save/Share placeholders, hidden where the article has no actions (e.g. previews)
  showActions?: boolean;
}

// Mirrors the ArticleContentDisplay layout so the page doesn't shift when the article loads
export function ContentArticleViewSkeleton({
  showActions = true,
}: ContentArticleViewSkeletonProps) {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isSmallScreen = useIsSmallScreen();

  const textLine = (width: string) => (
    <Skeleton animation="wave" variant="text" width={width} sx={{ fontSize: theme.spacing(1.75) }} />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5) }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
        {/* Topic tags */}
        <Box sx={{ display: 'flex', gap: theme.spacing(0.75) }}>
          {[theme.spacing(13), theme.spacing(8)].map((width) => (
            <Skeleton
              key={width}
              animation="wave"
              variant="rounded"
              width={width}
              height={theme.spacing(2.75)}
              sx={{ borderRadius: theme.spacing(1250) }}
            />
          ))}
        </Box>
        {/* Title */}
        <Box>
          <Skeleton animation="wave" variant="text" width="85%" sx={{ fontSize: theme.spacing(4) }} />
          {!isDesktop &&
            <Skeleton animation="wave" variant="text" width="60%" sx={{ fontSize: theme.spacing(4) }} />
          }
        </Box>
        {/* Standfirst */}
        <Skeleton animation="wave" variant="text" width="60%" sx={{ fontSize: theme.spacing(2) }} />
        {/* Byline & actions */}
        <Box sx={{
          display: 'flex',
          // Save/Share sit right of the byline; they only drop to their own row on phones
          flexDirection: isSmallScreen ? 'column' : 'row',
          alignItems: isSmallScreen ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          // On phones the byline and buttons are two 10px-padded rows 12px apart
          gap: isSmallScreen ? theme.spacing(4) : theme.spacing(2.5),
          py: theme.spacing(1.25),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Skeleton animation="wave" variant="circular" width={theme.spacing(3)} height={theme.spacing(3)} />
            <Skeleton animation="wave" variant="text" width={theme.spacing(22)} sx={{ fontSize: theme.spacing(1.5) }} />
          </Box>
          {showActions &&
          <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
            {[theme.spacing(8.375), theme.spacing(10.125)].map((width) => (
              <Skeleton
                key={width}
                animation="wave"
                variant="rounded"
                width={width}
                height={theme.spacing(4.5)}
                sx={{ borderRadius: theme.spacing(1) }}
              />
            ))}
          </Box>
          }
        </Box>
      </Box>
      {/* Hero */}
      <Skeleton
        animation="wave"
        variant="rounded"
        width="100%"
        height={theme.spacing(47.375)}
        sx={{ borderRadius: theme.spacing(0.75) }}
      />
      {/* Body */}
      <Box>
        {textLine('100%')}
        {textLine('100%')}
        {textLine('70%')}
      </Box>
      <Skeleton animation="wave" variant="text" width="35%" sx={{ fontSize: theme.spacing(3) }} />
      <Box>
        {textLine('100%')}
        {textLine('90%')}
      </Box>
    </Box>
  );
}
